import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { debounce } from 'lodash';
import { AnnouncementItem, ANNOUNCEMENT_CATEGORY } from 'types';
import { Button, Checkbox } from 'carbon-components-react';
import {
  Bullhorn24,
  Coronavirus24,
  Clean24,
  Help24,
  Idea24,
  Information24,
  Pin24,
  Tag24,
  Tools24,
  Warning24,
  Launch16,
  Close16,
  EventSchedule24,
  FaceSatisfied24,
  FaceDissatisfied24,
  CheckmarkOutline24,
  WarningAlt24
} from '@carbon/icons-react';
import {
  removeAnnouncement,
  removeAllAnnouncements,
  hideAnnouncementStart,
  unhideAnnouncementStart
} from 'redux/announcement';

const Icon: React.FC<{ icon?: string; category: string }> = ({
  icon,
  category
}) => {
  switch (icon) {
    case 'Bullhorn': {
      return <Bullhorn24 />;
    }
    case 'Coronavirus': {
      return <Coronavirus24 />;
    }
    case 'Clean': {
      return <Clean24 />;
    }
    case 'Help': {
      return <Help24 />;
    }
    case 'Idea': {
      return <Idea24 />;
    }
    case 'Information': {
      return <Information24 />;
    }
    case 'Pin': {
      return <Pin24 />;
    }
    case 'Tag': {
      return <Tag24 />;
    }
    case 'Tools': {
      return <Tools24 />;
    }
    case 'Warning': {
      return <Warning24 />;
    }
    case 'EventSchedule': {
      return <EventSchedule24 />;
    }
    case 'FaceSatisfied': {
      return <FaceSatisfied24 />;
    }
    case 'FaceDissatisfied': {
      return <FaceDissatisfied24 />;
    }
    case 'CheckmarkOutline': {
      return <CheckmarkOutline24 />;
    }
    case 'WarningAlt': {
      return <WarningAlt24 />;
    }
    default: {
      if (category === ANNOUNCEMENT_CATEGORY.INFORMATION) {
        return <Idea24 />;
      } else {
        return <Warning24 />;
      }
    }
  }
};

const AnnouncementCard: React.FC<Props> = ({ data }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const openLink = (url?: string) => {
    if (url === '/emails/undelivered') {
      history.push(url);
      dispatch(removeAllAnnouncements());
    } else {
      window.open(url, '_blank');
    }
  };

  /**
   * WHY: Debounced so that rapid check/uncheck sequences only fire one API call
   * based on the final state, preventing a race condition where an earlier in-flight
   * request could resolve after a later one and leave the server state out of sync.
   *
   * useCallback ensures the debounced function instance is stable across re-renders,
   * which is required for debounce to work correctly inside a React component.
   */
  const handleCheckboxChange = useCallback(
    debounce((value: boolean, id: number) => {
      if (value) {
        dispatch(hideAnnouncementStart(id));
      } else {
        dispatch(unhideAnnouncementStart(id));
      }
    }, 300),
    [dispatch]
  );

  return (
    <div className={`announcements-card ${data.category.toLowerCase()}`}>
      <div className="announcements-card-content">
        <div className="announcements-card-icon">
          <Icon icon={data.icon} category={data.category} />
        </div>
        <div className="announcements-card-info">
          <div className="title card-info-item">{data.title}</div>
          {data.text && (
            <div
              className="text card-info-item"
              dangerouslySetInnerHTML={{ __html: data.text }}
            />
          )}
          {data.callToActionLink && (
            <Button
              className="button card-info-item --swx-theme--dark"
              renderIcon={Launch16}
              kind="tertiary"
              size="field"
              onClick={() => openLink(data.callToActionLink)}
            >
              {data.callToActionLabel}
            </Button>
          )}
          {data.isUserAbleToHide && (
            <Checkbox
              labelText="Don't show again"
              id="card-content-hidden-checked"
              className="hidden-checked card-info-item --swx-theme--dark"
              onChange={(value: boolean) =>
                handleCheckboxChange(value, data.id)
              }
            />
          )}
        </div>
      </div>
      <div className="announcements-card-close">
        <button
          className="bx--modal-close"
          type="button"
          title="Close"
          aria-label="Close"
          onClick={() => dispatch(removeAnnouncement(data.id))}
        >
          <Close16 />
        </button>
      </div>
    </div>
  );
};

AnnouncementCard.displayName = 'AnnouncementCard';

type Props = {
  data: AnnouncementItem;
};

export default React.memo(AnnouncementCard);
